import React, { useEffect, useState } from 'react';
import {
  Button,
  Modal,
  Table,
  Typography,
  Form,
  InputNumber,
  Row,
  Col,
  Divider,
  Switch,
} from 'antd';
import apiService from '../api/apiService';
import html2pdf from 'html2pdf.js';
import { toast } from 'react-toastify';
import { initializeRoomTypes } from '../utils/getRoomTypeName';

const { Title, Text } = Typography;

const InvoiceModal = ({ currentBooking, hotelId, onInvoiceUpdate }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [guests, setGuests] = useState([]);
  const [roomOrders, setRoomOrders] = useState([]);
  const [invoiceItems, setInvoiceItems] = useState([]);
  const [accommodation, setAccommodation] = useState([]);
  const [form] = Form.useForm();
  const [tax, setTax] = useState(0);
  const [invoice, setInvoice] = useState(null);
  const [hotelDetails, setHotelDetails] = useState({});
  const [extraBedCharge, setExtraBedCharge] = useState(null);
  const [cwobCharge, setCwobCharge] = useState(null);
  const [planType, setPlanType] = useState([]);
  const [allInvoices, setAllInvoices] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [logoDataUrl, setLogoDataUrl] = useState(''); // Store logo as data URL

  useEffect(() => {
    fetchGuests();
    fetchHotelDetails();
    fetchPlanType();
    fetchInvoices();

    const load = async () => {
      await initializeRoomTypes();
    };
    load();
  }, []);

  // Convert image URL to data URL for PDF embedding
  const convertImageToDataUrl = imgUrl => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'Anonymous'; // Enable CORS if needed

      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;

          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);

          const dataURL = canvas.toDataURL('image/png');
          resolve(dataURL);
        } catch (err) {
          reject(err);
        }
      };

      img.onerror = err => {
        reject(new Error('Error loading image'));
      };

      img.src = imgUrl;
    });
  };

  const fetchHotelDetails = async () => {
    try {
      const response = await apiService.get(`/hotels/${hotelId}/`);
      setHotelDetails(response || {});

      // Convert logo to data URL when hotel details are fetched
      if (response?.logo) {
        try {
          const dataUrl = await convertImageToDataUrl(response.logo);
          setLogoDataUrl(dataUrl);
        } catch (error) {
          console.error('Failed to convert logo to data URL:', error);
        }
      }
    } catch (error) {
      console.error('Failed to fetch hotel details:', error);
    }
  };

  const fetchGuests = async () => {
    try {
      const response = await apiService.get(`/guests/?hotel=${hotelId}`);
      setGuests(response);
    } catch (error) {
      console.error('Failed to fetch guests:', error);
    }
  };

  const fetchRoomOrders = async () => {
    if (!currentBooking?.id) return;

    try {
      // Fetch all room orders for the hotel
      const response = await apiService.get(`/room-orders/?hotel=${hotelId}`);

      // Filter room orders for the current booking on the client side
      const filteredOrders = response.filter(
        order => order.booking === currentBooking.id
      );

      setRoomOrders(filteredOrders);

      // Convert room orders to invoice items
      const roomOrderItems = filteredOrders.map(order => ({
        id: order.id,
        type: 'room-order',
        item: order.order_details,
        quantity: order.quantity || 'NA',
        price: parseFloat(order.amount),
        total: parseFloat(order.amount) * (order.quantity || 'NA'),
        key: `room-order-${order.id}`,
      }));

      setInvoiceItems(roomOrderItems);
    } catch (error) {
      console.error('Failed to fetch room orders:', error);
    }
  };

  const fetchPlanType = async () => {
    if (!currentBooking?.plan_type) return;

    try {
      const response = await apiService.get(
        `/plan-type/${currentBooking.plan_type}/?hotel=${hotelId}`
      );
      setPlanType(response);
    } catch (error) {
      console.error('Failed to fetch plan type:', error);
    }
  };

  const calculateNights = (checkIn, checkOut) => {
    return Math.max(
      1,
      Math.round(
        (new Date(checkOut) - new Date(checkIn)) / (1000 * 60 * 60 * 24)
      )
    );
  };

  const fetchAccommodationPrice = async () => {
    try {
      const roomPrices = await apiService.get(`/room-prices/?hotel=${hotelId}`);

      if (!currentBooking?.room || !Array.isArray(currentBooking.room)) {
        console.error('No room data available in booking');
        return;
      }

      const matchedPrices = currentBooking.room.map(room => {
        const priceEntry = roomPrices.find(
          roomPrice => roomPrice.room === room.id
        );

        return {
          room_id: room.id,
          room_number: room.room_number,
          room_type: room.room_type_name,
          price: priceEntry ? priceEntry.amount : 0,
        };
      });

      const nights = calculateNights(
        currentBooking.check_in,
        currentBooking.check_out
      );

      // Group rooms by room type
      const roomTypeGroups = {};

      matchedPrices.forEach(room => {
        const roomPrice = Number(room.price);
        const planPrice = room.room_type === 'FOC' ? 0 : Number(planType.price);
        const totalRoomPricePerNight = roomPrice + planPrice;

        if (!roomTypeGroups[room.room_type]) {
          roomTypeGroups[room.room_type] = {
            type: 'accommodation',
            roomType: room.room_type,
            nights: nights,
            planType: planType.title,
            quantity: 1,
            price: totalRoomPricePerNight,
            total: totalRoomPricePerNight * nights, // initial total for 1 room
          };
        } else {
          roomTypeGroups[room.room_type].quantity += 1;
          roomTypeGroups[room.room_type].total +=
            totalRoomPricePerNight * nights;
        }
      });

      // Convert the grouped object to an array
      const accommodationItems = Object.values(roomTypeGroups);
      setAccommodation(accommodationItems);
    } catch (error) {
      console.error('Failed to fetch accommodation prices:', error);
    }
  };

  const fetchExtraBedCharge = async () => {
    try {
      if (!currentBooking?.plan_type) return;

      const extraBedPrice = planType?.extra_bed_price || 0;
      const extraBedCount = currentBooking?.extra_bed || 0;

      const cwobPrice = planType?.cwob_price || 0;
      const cwobCount = currentBooking?.cwob || 0;

      const nights = calculateNights(
        currentBooking.check_in,
        currentBooking.check_out
      );

      if (extraBedCount > 0) {
        const extraBedTotal = extraBedCount * extraBedPrice * nights;
        setExtraBedCharge({
          type: 'extra-bed',
          item: `Extra Bed`,
          planType: planType.title || '',
          quantity: extraBedCount,
          duration: `${nights}N`,
          price: extraBedPrice,
          key: 'extra-bed',
          total: extraBedTotal,
        });
      } else {
        setExtraBedCharge(null);
      }

      if (cwobCount > 0) {
        const cwobTotal = cwobCount * cwobPrice * nights;
        setCwobCharge({
          type: 'cwob',
          item: `CWOB`,
          planType: planType.title || '',
          quantity: cwobCount,
          duration: `${nights}N`,
          price: cwobPrice,
          key: 'cwob',
          total: cwobTotal,
        });
      } else {
        setCwobCharge(null);
      }
    } catch (error) {
      console.error(
        'Failed to fetch plan-type or calculate extra bed charge',
        error
      );
    }
  };

  const getGuest = () => {
    return guests.find(guest => guest.id === currentBooking?.guest) || {};
  };
  const guest = getGuest();

  // Handle price changes for any item
  const handlePriceChange = (itemKey, newPrice) => {
    if (itemKey.startsWith('acc-')) {
      // Handle accommodation price change
      const roomType = itemKey.replace('acc-', '');
      setAccommodation(prevAccommodation =>
        prevAccommodation.map(item => {
          if (item.roomType === roomType) {
            const newTotal = item.quantity * newPrice * item.nights;
            return { ...item, price: newPrice, total: newTotal };
          }
          return item;
        })
      );
    } else if (itemKey === 'extra-bed') {
      // Handle extra bed price change
      if (extraBedCharge) {
        const nights = calculateNights(
          currentBooking.check_in,
          currentBooking.check_out
        );
        const newTotal = extraBedCharge.quantity * newPrice * nights;
        setExtraBedCharge({
          ...extraBedCharge,
          price: newPrice,
          total: newTotal,
        });
      }
    } else if (itemKey === 'cwob') {
      // Handle CWOB price change
      if (cwobCharge) {
        const nights = calculateNights(
          currentBooking.check_in,
          currentBooking.check_out
        );
        const newTotal = cwobCharge.quantity * newPrice * nights;
        setCwobCharge({ ...cwobCharge, price: newPrice, total: newTotal });
      }
    } else if (itemKey.startsWith('inv-')) {
      // Handle invoice item price change
      const itemId = parseInt(itemKey.replace('inv-', ''));
      setInvoiceItems(prevItems =>
        prevItems.map(item => {
          if (item.id === itemId) {
            const newTotal = item.quantity * newPrice;
            return { ...item, price: newPrice, total: newTotal };
          }
          return item;
        })
      );
    } else if (itemKey.startsWith('room-order-')) {
      // Handle room order price change
      const orderId = parseInt(itemKey.replace('room-order-', ''));
      setInvoiceItems(prevItems =>
        prevItems.map(item => {
          if (item.id === orderId && item.type === 'room-order') {
            const newTotal = item.quantity * newPrice;
            return { ...item, price: newPrice, total: newTotal };
          }
          return item;
        })
      );
    }
  };

  const calculateSubtotal = () => {
    let total = 0;

    // Add accommodation prices
    accommodation.forEach(item => {
      total += parseFloat(item.total || 0);
    });

    // Add extra bed charges if any
    if (extraBedCharge) {
      total += parseFloat(extraBedCharge.total || 0);
    }

    // Add CWOB charges if any
    if (cwobCharge) {
      total += parseFloat(cwobCharge.total || 0);
    }

    // Add invoice items (including room orders)
    invoiceItems.forEach(item => {
      total += parseFloat(item.total || 0);
    });

    return total;
  };

  const getDiscount = () => {
    return parseFloat(currentBooking?.discount || 0);
  };

  const getAdvance = () => {
    return parseFloat(currentBooking?.advance || 0);
  };

  const columns = [
    {
      title: 'Item',
      dataIndex: 'item',
    },
    {
      title: 'Plan Type',
      dataIndex: 'planType',
    },
    {
      title: 'Duration/Stay',
      dataIndex: 'duration',
    },
    {
      title: 'Qty',
      dataIndex: 'quantity',
    },
    {
      title: 'Price',
      dataIndex: 'price',
      render: (text, record) => {
        return editMode ? (
          <InputNumber
            min={0}
            controls={false}
            value={parseFloat(text || 0)}
            onChange={value => handlePriceChange(record.key, value)}
            formatter={value => `Rs. ${value}`}
            parser={value => value.replace(/Rs\.\s?/g, '')}
            style={{ width: '100px' }}
          />
        ) : (
          <Text>{`Rs. ${parseFloat(text || 0).toFixed(2)}`}</Text>
        );
      },
    },
    {
      title: 'Total',
      dataIndex: 'total',
      render: text => `Rs. ${parseFloat(text || 0).toFixed(2)}`,
    },
  ];

  const combinedCharges = [
    ...accommodation.map(item => ({
      ...item,
      key: `acc-${item.roomType}`,
      item: item.roomType,
      planType: item.planType || planType.name,
      duration: `${item.nights}N`,
    })),
    ...(extraBedCharge ? [extraBedCharge] : []),
    ...(cwobCharge ? [cwobCharge] : []),
    ...invoiceItems.map(item => ({
      ...item,
      key: item.key || `inv-${item.id}`,
      planType: item.planType || '',
      duration: item.duration || '',
    })),
  ];

  const calculateTotal = () => {
    const subtotal = calculateSubtotal(); // Room + Orders
    const discountAmount = getDiscount(); // Any applied discount
    const subtotalAfterDiscount = subtotal - discountAmount;

    const taxAmount = subtotalAfterDiscount * (tax / 100);
    const totalWithTax = subtotalAfterDiscount + taxAmount;

    return totalWithTax; // This is the actual invoice amount (before advance)
  };

  const calculateBalanceDue = () => {
    const total = calculateTotal();
    const advanceAmount = getAdvance(); // Already paid
    return total - advanceAmount;
  };

  const fetchInvoices = async () => {
    try {
      const response = await apiService.get(`/invoices/?hotel=${hotelId}`);
      setAllInvoices(response);
    } catch (error) {
      console.error('Failed to fetch invoices:', error);
    }
  };

  // Check if an invoice already exists for this booking
  const fetchExistingInvoice = async () => {
    try {
      const existingInvoice = allInvoices.find(
        inv => inv.booking === currentBooking.id
      );

      if (existingInvoice) {
        setInvoice(existingInvoice);
        if (existingInvoice.tax) {
          setTax(existingInvoice.tax);
        }
        return existingInvoice;
      }
      return null;
    } catch (error) {
      console.error('Failed to fetch existing invoice:', error);
      return null;
    }
  };

  const saveInvoice = async () => {
    try {
      await form.validateFields();
      const totalAmount = calculateTotal();

      // Find the existing invoice for this booking
      const existingInvoice = allInvoices.find(
        inv => inv.booking === currentBooking.id
      );

      let invoiceId = existingInvoice?.id;

      if (!invoiceId) {
        console.log('Creating new invoice for booking:', currentBooking.id);
        const createdInvoice = await apiService.post(
          `/invoices/?hotel=${hotelId}`,
          {
            booking: currentBooking.id,
            total_amount: totalAmount,
            tax: tax,
            hotel: hotelId,
          }
        );
        onInvoiceUpdate();
        invoiceId = createdInvoice.id;
        setInvoice(createdInvoice);

        // After creating invoice, add each room order as an invoice detail
        for (const item of invoiceItems) {
          if (item.type === 'room-order') {
            await apiService.post(`/invoice-details/?hotel=${hotelId}`, {
              invoice: invoiceId,
              room_order: item.id,
              particulars: item.item,
              amount: item.total,
              hotel: hotelId,
            });
          }
        }
      } else {
        // Update existing invoice record
        console.log('Updating existing invoice:', invoiceId);
        await apiService.patch(`/invoices/${invoiceId}/?hotel=${hotelId}`, {
          booking: currentBooking.id,
          total_amount: totalAmount,
          tax: tax,
        });
        onInvoiceUpdate();

        // Get existing invoice details
        const existingDetails = await apiService.get(
          `/invoice-details/?invoice=${invoiceId}&hotel=${hotelId}`
        );

        // Update existing invoice details or create new ones as needed
        for (const item of invoiceItems) {
          if (item.type === 'room-order') {
            const existing = existingDetails.find(
              d => d.room_order === item.id
            );

            if (existing) {
              // Update existing invoice detail
              await apiService.patch(
                `/invoice-details/${existing.id}/?hotel=${hotelId}`,
                {
                  particulars: item.item,
                  amount: item.total,
                }
              );
            } else {
              // Create new invoice detail
              await apiService.post(`/invoice-details/?hotel=${hotelId}`, {
                invoice: invoiceId,
                room_order: item.id,
                particulars: item.item,
                amount: item.total,
                hotel: hotelId,
              });
            }
          }
        }
      }

      // Refresh the invoices list after saving
      fetchInvoices();
      toast.success('Invoice saved successfully');

      // Turn off edit mode after saving
      setEditMode(false);
    } catch (error) {
      console.error('Failed to save invoice:', error);
      toast.error('Failed to generate invoice');
    }
  };

  // Function to upload PDF as a file to the server
  const uploadPdfToServer = async (pdfBlob, invoiceId) => {
    try {
      // Create a FormData instance
      const formData = new FormData();

      // Create a File object from the Blob
      const pdfFile = new File(
        [pdfBlob],
        `invoice-${currentBooking?.id || 'hotel'}.pdf`,
        {
          type: 'application/pdf',
        }
      );

      // Append the file to FormData
      formData.append('ivoice_file', pdfFile);

      // Send PATCH request to update the invoice with the PDF file
      await apiService.patch(
        `/invoices/${invoiceId}/?hotel=${hotelId}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      onInvoiceUpdate();

      toast.success('Invoice PDF uploaded successfully');
    } catch (error) {
      console.error('Failed to upload PDF to server:', error);
      toast.error('Failed to upload invoice PDF');
    }
  };

  const downloadPDF = async () => {
    // Temporarily turn off edit mode for PDF
    const currentEditMode = editMode;
    setEditMode(false);

    try {
      // Calculate total amount
      const totalAmount = calculateTotal();

      // Find existing invoice for this booking
      const existingInvoice = allInvoices.find(
        inv => inv.booking === currentBooking?.id
      );

      let currentInvoiceId;

      // Generate the PDF first
      const element = document.getElementById('invoice-content');

      const options = {
        margin: 0.5,
        filename: `invoice-${currentBooking?.id || 'hotel'}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          logging: false,
        },
        jsPDF: {
          unit: 'in',
          format: 'a4',
          orientation: 'portrait',
          putOnlyUsedFonts: true,
          compress: true,
          hotfixes: ['px_scaling'],
        },
        documentProperties: {
          title: `Invoice #${currentBooking?.id || ''}`,
          subject: 'Hotel Invoice',
          author: 'Hotel Management System',
          keywords: 'invoice',
          creator: 'PDF Generator',
        },
        output: 'blob',
      };

      // Generate PDF as blob
      const pdfBlob = await html2pdf()
        .from(element)
        .set(options)
        .output('blob');

      // Create a File object from the Blob
      const pdfFile = new File(
        [pdfBlob],
        `invoice-${currentBooking?.id || 'hotel'}.pdf`,
        {
          type: 'application/pdf',
        }
      );

      // Create FormData instance for API request
      const formData = new FormData();
      formData.append('ivoice_file', pdfFile);
      formData.append('total_amount', totalAmount);
      formData.append('booking', currentBooking.id);
      formData.append('hotel', hotelId);

      // If no invoice exists, create one
      if (!existingInvoice) {
        try {
          const response = await apiService.post(
            `/invoices/?hotel=${hotelId}`,
            formData,
            {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            }
          );
          currentInvoiceId = response.id;
          setInvoice(response);
          onInvoiceUpdate();

          // Add each room order as an invoice detail
          for (const item of invoiceItems) {
            if (item.type === 'room-order') {
              await apiService.post(`/invoice-details/?hotel=${hotelId}`, {
                invoice: currentInvoiceId,
                room_order: item.id,
                particulars: item.item,
                amount: item.total,
                hotel: hotelId,
              });
            }
          }

          toast.success('Invoice created successfully with PDF');
        } catch (error) {
          console.error('Failed to create invoice:', error);
          toast.error('Failed to create invoice');
          setEditMode(currentEditMode);
          return;
        }
      } else {
        // Update existing invoice with PDF
        currentInvoiceId = existingInvoice.id;

        try {
          await apiService.patch(
            `/invoices/${currentInvoiceId}/?hotel=${hotelId}`,
            formData,
            {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            }
          );
          onInvoiceUpdate();
          toast.success('Invoice updated successfully with PDF');
        } catch (error) {
          console.error('Failed to update invoice:', error);
          toast.error('Failed to update invoice with PDF');
          setEditMode(currentEditMode);
          return;
        }
      }

      // Refresh invoices list
      fetchInvoices();

      // Also trigger download for the user
      const pdfUrl = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = `invoice-${currentBooking?.id || 'hotel'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(pdfUrl);

      // Restore edit mode after PDF generation
      setEditMode(currentEditMode);
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error('Error generating PDF');
      setEditMode(currentEditMode);
    }
  };

  const handleModalOpen = async () => {
    setModalOpen(true);
    setEditMode(false);

    setInvoiceItems([]);
    setAccommodation([]);
    setInvoice(null);

    try {
      // First check if an invoice already exists for this booking
      const existingInvoice = await fetchExistingInvoice();

      await fetchPlanType();
      await fetchRoomOrders();
      await fetchAccommodationPrice();
      await fetchExtraBedCharge();
    } catch (error) {
      console.error('Error opening invoice modal:', error);
      toast.error('Failed to load invoice data');
    }
  };

  // Get existing invoice for this booking to determine create or update button text
  const existingInvoice = allInvoices.find(
    inv => inv.booking === currentBooking?.id
  );

  // Toggle edit mode
  const toggleEditMode = () => {
    setEditMode(prev => !prev);
  };

  return (
    <>
      <button onClick={handleModalOpen}>View Invoice</button>

      <Modal
        title="Invoice Preview"
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        width={900}
        style={{ top: 20 }}
        footer={[
          <Button
            key="download"
            onClick={downloadPDF}
            /* disabled={!existingInvoice?.id} */
            title={
              !existingInvoice?.id
                ? 'Save invoice first to enable download'
                : ''
            }
          >
            {existingInvoice?.ivoice_file
              ? 'Update & Download PDF'
              : 'Generate & Download PDF'}
          </Button>,
          <Button
            key="toggle-edit"
            type={editMode ? 'default' : 'primary'}
            onClick={toggleEditMode}
          >
            {editMode ? 'Cancel Edit' : 'Edit Prices'}
          </Button>,
          editMode && (
            <Button key="save" type="primary" onClick={saveInvoice}>
              {existingInvoice ? 'Update' : 'Save'}
            </Button>
          ),
          <Button key="close" onClick={() => setModalOpen(false)}>
            Close
          </Button>,
        ]}
      >
        <Form form={form} layout="vertical" initialValues={{ tax }}>
          <div
            id="invoice-content"
            style={{ padding: '1rem', background: '#fff' }}
          >
            <Row align="start" gutter={[16, 16]}>
              <Col xs={24} md={12}>
                <div
                  style={{
                    width: '200px',
                    height: '200px',
                    objectFit: 'cover',
                  }}
                >
                  {/* Use the data URL version of the logo for better PDF compatibility */}
                  {logoDataUrl ? (
                    <img
                      src={logoDataUrl}
                      alt="Hotel Logo"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px dashed #ccc',
                      }}
                    >
                      {hotelDetails.name || 'Hotel Logo'}
                    </div>
                  )}
                </div>
              </Col>
              <Col xs={24} md={12}>
                <Text>
                  Invoice Date: {new Date().toISOString().split('T')[0]}
                </Text>
                <br />
                <Text>Booking ID: {currentBooking?.id}</Text>
              </Col>
            </Row>

            <Divider />

            <Row gutter={16}>
              <Col span={12}>
                <Title level={4}>Bill From</Title>
                <Text>{hotelDetails.name || ''}</Text>
                <br />
                <Text>
                  {hotelDetails.address || ''},&nbsp;{hotelDetails.city || ''}
                  ,&nbsp;
                  {hotelDetails.country || ''},&nbsp;
                  {hotelDetails.zip_code || ''}
                </Text>
                <br />
                <Text>{hotelDetails.email || ''}</Text>
                <br />
                <Text>{hotelDetails.phone || ''}</Text>
                <br />
                <br />
              </Col>
              <Col span={12}>
                <Title level={4}>Bill To</Title>
                <Text>{guest.name || ''}</Text>
                <br />
                <Text>Email: {guest.email || ''}</Text>
                <br />
                <Text>Phone: {guest.phone || ''}</Text>
                <br />
                <Text>Check-In: {currentBooking?.check_in || ''}</Text>
                <br />
                <Text>Check-Out: {currentBooking?.check_out || ''}</Text>
              </Col>
            </Row>

            <Divider />

            <Table
              dataSource={combinedCharges}
              columns={columns}
              pagination={false}
              bordered
              size="small"
              scroll={{ x: 'max-content' }}
              summary={() => {
                const subtotal = calculateSubtotal();
                const discountAmount = getDiscount();
                const advanceAmount = getAdvance();

                const subtotalAfterDiscount = subtotal - discountAmount;
                const taxAmount = subtotalAfterDiscount * (Number(tax) / 100);
                const total = subtotalAfterDiscount + taxAmount;
                const balanceDue = total - advanceAmount;

                return (
                  <>
                    <Table.Summary.Row>
                      <Table.Summary.Cell colSpan={5}>
                        <Text strong>Subtotal</Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell>
                        <Text>Rs. {subtotal.toFixed(2)}</Text>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>

                    {discountAmount > 0 && (
                      <Table.Summary.Row>
                        <Table.Summary.Cell colSpan={5}>
                          <Text strong>Discount</Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell>
                          <Text style={{ color: 'red' }}>
                            - Rs. {discountAmount.toFixed(2)}
                          </Text>
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    )}

                    <Table.Summary.Row>
                      <Table.Summary.Cell colSpan={5}>
                        <Text strong>Tax ({tax}%)</Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell>
                        <Text>Rs. {taxAmount.toFixed(2)}</Text>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>

                    <Table.Summary.Row>
                      <Table.Summary.Cell colSpan={5}>
                        <Text strong>Total (incl. Tax)</Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell>
                        <Text strong>Rs. {total.toFixed(2)}</Text>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>

                    {advanceAmount > 0 && (
                      <Table.Summary.Row>
                        <Table.Summary.Cell colSpan={5}>
                          <Text strong>Advance Paid</Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell>
                          <Text style={{ color: 'green' }}>
                            - Rs. {advanceAmount.toFixed(2)}
                          </Text>
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    )}

                    <Table.Summary.Row>
                      <Table.Summary.Cell colSpan={5}>
                        <Text strong>Balance Due</Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell>
                        <Text strong>Rs. {balanceDue.toFixed(2)}</Text>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  </>
                );
              }}
            />
          </div>

          <div style={{ marginTop: 24 }}>
            <Form.Item label="Tax (%)" name="tax">
              <InputNumber
                controls={false}
                min={0}
                max={100}
                value={tax}
                onChange={value => setTax(value || 0)}
                disabled={!editMode}
              />
            </Form.Item>
          </div>
        </Form>
      </Modal>
    </>
  );
};

export default InvoiceModal;
